import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from "formik";
import { toast } from 'react-hot-toast';
import * as Yup from "yup";
import axios from 'axios';
import ReCAPTCHA from "react-google-recaptcha";

import Header from '../../HomePage/Header/Header';
import Footer from '../../HomePage/Footer/Footer';
import FeedbackForm from '../../FeedbackForm/FeedbackForm';
import HourglassLoader from '../../../Common/HourglassLoader';

const host = process.env.REACT_APP_API_SERVER;
const siteKey = process.env.REACT_APP_SITE_KEY;

const TutorLogin = () => {
    const navigate = useNavigate();
    const captchaRef = useRef(null);

    const [faIcon, setFaIcon] = useState('fa-eye-slash');
    const [captchaToken, setCaptchaToken] = useState(null);
    const [captchaError, setCaptchaError] = useState("");

    const togglePassword = () => {
        const input = document.getElementById("tutor-password");
        if (input.type === "password") {
            input.type = "text";
            setFaIcon("fa-eye");
        } else {
            input.type = "password";
            setFaIcon("fa-eye-slash");
        }
    };

    const formik = useFormik({
        initialValues: {
            emailMob: "",
            password: "",
            user_type: "tutor"
        },
        validationSchema: Yup.object({
            emailMob: Yup.string()
                .required("Email is required.")
                .matches(/^\w+[-.\w]*@(?!(?:)\.com$)\w+[-.\w]*?\.\w{2,4}$/, "Enter a valid Email"),
            password: Yup.string().required("Password is required")
        }),
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            if (!captchaToken) {
                setCaptchaError("Please complete the CAPTCHA");
                return;
            }

            try {
                setSubmitting(true);
                
                const response = await axios.post(
                    `${host}/api/web/tutor-sign-in`,
                    {
                        ...values,
                        captchaToken
                    }
                );

                if (response.status === 200) {
                    resetForm();

                    localStorage.removeItem("student-token");
                    localStorage.setItem("tutor-token", response.data.token);

                    await axios.get(
                        `${host}/api/web/change-online-status?status=1`,
                        {
                            headers: {
                                "x-auth-token": response.data.token
                            }
                        }
                    );

                    toast.success("Signed In Successfully");
                    navigate("/tutor");
                } else {
                    toast.error(response.data.message);
                    if (captchaRef.current) {
                        captchaRef.current.reset();
                    }
                    setCaptchaToken(null);
                }
            } catch (error) {
                console.error("Login Error:", error);
                toast.error(error.response?.data?.message || "Login Failed");
                if (captchaRef.current) {
                    captchaRef.current.reset();
                }
                setCaptchaToken(null);
            } finally {
                setSubmitting(false);
            }
        }
    });

    const handleCaptcha = (token) => {
        setCaptchaToken(token);
        setCaptchaError("");
    };

    return (
        <>
            <FeedbackForm />
            <Header />

            {/* Same background as student login */}
            <div className="login-background">
                <div className="login-main-container login-main-container-two">
                    <div className="row login-form-part">
                        <div className="card login-form" style={{ maxWidth: "500px", margin: "0 auto" }}>
                            <div className="form-body pt-4">
                                <form onSubmit={formik.handleSubmit}>
                                    <h4 className="text-center mb-4 fw-bold">Tutor Login</h4>

                                    {/* Email Field */}
                                    <div className="mb-3">
                                        <label htmlFor="emailMob" className="form-label">
                                            Email<span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="emailMob"
                                            id="emailMob"
                                            placeholder="Enter Your Email"
                                            className="input-form"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.emailMob}
                                        />
                                        <div className="help-block with-errors">
                                            {formik.touched.emailMob && formik.errors.emailMob && (
                                                <div className="text-danger mt-1">
                                                    {formik.errors.emailMob}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Password Field */}
                                    <div className="mb-3">
                                        <label htmlFor="tutor-password" className="form-label">
                                            Password<span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <input
                                                placeholder="Enter Your Password"
                                                id="tutor-password"
                                                type="password"
                                                className="input-form"
                                                name="password"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.password}
                                            />
                                            <i
                                                toggle="#tutor-password"
                                                className={`fa-regular ${faIcon} field-icon toggle-password`}
                                                id="toggle_pass"
                                                onClick={togglePassword}
                                            />
                                        </div>
                                        <div className="help-block with-errors">
                                            {formik.touched.password && formik.errors.password && (
                                                <div className="text-danger mt-1">
                                                    {formik.errors.password}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Forgot Password Link */}
                                    <div className="mb-3 text-end">
                                        <Link
                                            to="/forget-password"
                                            className="text-primary text-decoration-underline"
                                            style={{ fontSize: 14 }}
                                        >
                                            Forgot Your Password?
                                        </Link>
                                    </div>

                                    {/* Recaptcha */}
                                    <div className="mb-3">
                                        <ReCAPTCHA
                                            sitekey={siteKey}
                                            onChange={handleCaptcha}
                                            ref={captchaRef}
                                        />
                                        {captchaError && (
                                            <div className="text-danger mt-2">{captchaError}</div>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <div className="mb-3 row">
                                        <div className="col-sm-12 mb-3 text-center">
                                            <button
                                                type="submit"
                                                disabled={formik.isSubmitting}
                                                className="login-btn fw-bold"
                                                style={{ color: "white !important" }}
                                            >
                                                {formik.isSubmitting ? <HourglassLoader /> : "Login"}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Register Link */}
                                    <div className="log-undre-text text-center my-2">
                                        <p>
                                            Not registered yet?
                                            <Link to="/tutor/register">
                                                <span> Register</span>
                                            </Link>
                                        </p>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default TutorLogin;